/**
 * 透视表 行/列 维度「数据处理函数」注册表
 * ----------------------------------------------------------------
 * 用途：在透视表配置中，为某个维度字段指定一个处理函数，
 *       在数据进入 VTable 前对原始值做转换（典型场景：把时间字符串
 *       处理成「年 / 年月 / 年月日」，从而实现按年、按月分组）。
 *
 * 设计原则：
 * - 配置中只保存函数的「key 字符串」（可 JSON 序列化，便于存入视图）；
 *   运行时通过 getPivotFieldProcessFunc(key) 取到真正的函数。
 * - 新增处理函数只需在 PIVOT_FIELD_PROCESS_FUNCS 中追加一项，UI 自动出现。
 */

import dayjs from 'dayjs';
import * as numfmt from 'numfmt';

/** 数据处理函数签名：输入原始值，输出处理后的值 */
export type PivotFieldProcessFunc = (value: any) => any;

/** 单个处理函数的元信息（用于 UI 下拉与运行时查找） */
export interface PivotProcessFuncDef {
  key: string; // 配置中保存的函数标识
  label: string; // 下拉框展示名称
  func: PivotFieldProcessFunc; // 实际处理函数
}

/**
 * 将各种形态的时间值归一化为 dayjs 对象
 * 支持：Date 对象、秒/毫秒时间戳、'YYYY-MM-DD'、'YYYY-MM-DD HH:mm:ss'、
 *       ISO 字符串（含 T 与时区）等。
 */
const normalizeDate = (value: any): dayjs.Dayjs | null => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return dayjs(value);
  if (typeof value === 'number') {
    if (!isFinite(value)) return null;
    // 10 位秒级时间戳 → 毫秒
    const ms = value < 1e12 ? value * 1000 : value;
    const d = dayjs(ms);
    return d.isValid() ? d : null;
  }
  const s = String(value).trim();
  if (!s) return null;
  // 处理 ISO（2026-07-07T00:00:00.000Z）→ 取前 19 位并按本地时间解析
  const normalized = s.includes('T') ? s.replace('T', ' ').slice(0, 19) : s;
  const d = dayjs(normalized);
  return d.isValid() ? d : null;
};

/** 季度（1-4），dayjs 核心未内置 quarter，手动计算 */
const quarterOf = (d: dayjs.Dayjs): number => Math.floor(d.month() / 3) + 1;

export const PIVOT_FIELD_PROCESS_FUNCS: PivotProcessFuncDef[] = [
  {
    key: 'none',
    label: '不处理',
    func: v => v,
  },
  {
    key: 'date_year',
    label: '日期 → 年',
    func: v => {
      const d = normalizeDate(v);
      return d ? d.format('YYYY') : v;
    },
  },
  {
    key: 'date_year_quarter',
    label: '日期 → 年·季度',
    func: v => {
      const d = normalizeDate(v);
      return d ? `${d.format('YYYY')}Q${quarterOf(d)}` : v;
    },
  },
  {
    key: 'date_year_month',
    label: '日期 → 年月',
    func: v => {
      const d = normalizeDate(v);
      return d ? d.format('YYYY-MM') : v;
    },
  },
  {
    key: 'date_year_month_day',
    label: '日期 → 年月日',
    func: v => {
      const d = normalizeDate(v);
      return d ? d.format('YYYY-MM-DD') : v;
    },
  },
  {
    key: 'date_month',
    label: '日期 → 月',
    func: v => {
      const d = normalizeDate(v);
      return d ? d.format('MM') : v;
    },
  },
  {
    key: 'date_day',
    label: '日期 → 日',
    func: v => {
      const d = normalizeDate(v);
      return d ? d.format('DD') : v;
    },
  },
];

const FUNC_MAP = new Map<string, PivotProcessFuncDef>(
  PIVOT_FIELD_PROCESS_FUNCS.map(d => [d.key, d]),
);

/**
 * 按 key 获取处理函数。
 * - key 为空或等于 'none' 时返回 null（表示无需处理，数据原样使用）。
 * - key 未注册时返回 null（容错，避免配置异常导致整表崩溃）。
 */
export function getPivotFieldProcessFunc(key?: string | null): PivotFieldProcessFunc | null {
  if (!key || key === 'none') return null;
  return FUNC_MAP.get(key)?.func ?? null;
}

/**
 * 值字段「显示格式」注册表
 * ----------------------------------------------------------------
 * 用途：在透视表值配置中，为聚合后的值指定显示格式。
 * 三类格式：
 * - duration_seconds：秒 → 时间间隔（3天2小时15分钟40秒）
 * - duration_minutes：分钟 → 时间间隔
 * - numfmt：通用数字格式，使用 numfmt 库（ECMA-376 / Excel 标准格式串），
 *   具体格式串保存在值字段的 formatPattern 中（如 '#,##0.00'、'0.00%'）。
 * 兼容旧配置：历史保存的 key（'0.00' / '#,##0' / '¥0.00' 等）本身就是合法的
 * numfmt 格式串，formatPivotValue 会将未注册的 key 直接当作格式串处理。
 */

export interface PivotValueFormatDef {
  key: string; // 配置中保存的格式标识
  label: string; // 下拉框展示名称
  format: (value: number, pattern?: string) => string; // 格式化函数（numfmt 类需要 pattern）
}

/**
 * 将秒数转为可读的时间间隔，例如：3天2小时15分钟40秒500毫秒
 * - 支持负值（返回 "-X天..." 形式）。
 * - 输入为小数时，不足 1 秒的部分以「毫秒」呈现（四舍五入至整数毫秒），
 *   保证各分段之和尽量逼近原始秒数。
 */
const formatDurationFromSeconds = (totalSeconds: number | string): string => {
  if (totalSeconds === null || totalSeconds === undefined || totalSeconds === '')
    return String(totalSeconds);
  if (typeof totalSeconds !== 'number' || !isFinite(totalSeconds)) return String(totalSeconds);
  const isNeg = totalSeconds < 0;
  const totalMs = Math.round(Math.abs(totalSeconds) * 1000);
  const days = Math.floor(totalMs / 86400000);
  let rem = totalMs - days * 86400000;
  const hours = Math.floor(rem / 3600000);
  rem -= hours * 3600000;
  const minutes = Math.floor(rem / 60000);
  rem -= minutes * 60000;
  const seconds = Math.floor(rem / 1000);
  rem -= seconds * 1000;
  const ms = rem;
  const parts: string[] = [];
  if (days) parts.push(`${days}天`);
  if (hours) parts.push(`${hours}小时`);
  if (minutes) parts.push(`${minutes}分钟`);
  if (seconds) parts.push(`${seconds}秒`);
  if (ms) parts.push(`${ms}毫秒`);
  if (parts.length === 0) return '0秒';
  return (isNeg ? '-' : '') + parts.join('');
};

/** 通用数字格式的 key（选择此项后可通过 formatPattern 进一步配置格式串） */
export const NUMFMT_FORMAT_KEY = 'numfmt';

/** 通用数字格式的缺省格式串 */
export const DEFAULT_NUMFMT_PATTERN = '#,##0.00';

/**
 * 通用数字格式的常用格式串预设（供 a-auto-complete 下拉选择，也可自定义输入）。
 * 全部为 numfmt 库支持的 ECMA-376 / Excel 标准格式串。
 */
export const NUMFMT_PATTERN_PRESETS: { value: string; label?: string }[] = [
  { value: '0', label: '0（整数）' },
  { value: '0.0', label: '0.0（1 位小数）' },
  { value: '0.00', label: '0.00（2 位小数）' },
  { value: '0.000', label: '0.000（3 位小数）' },
  { value: '#,##0', label: '#,##0（千分位整数）' },
  { value: '#,##0.00', label: '#,##0.00（千分位 2 位小数）' },
  { value: '0%', label: '0%（百分比整数）' },
  { value: '0.00%', label: '0.00%（百分比 2 位小数）' },
  { value: '¥#,##0.00', label: '¥#,##0.00（人民币）' },
  { value: '$#,##0.00', label: '$#,##0.00（美元）' },
  { value: '0.00E+00', label: '0.00E+00（科学计数）' },
  { value: '# ?/?', label: '# ?/?（分数）' },
  { value: '#,##0;[Red]-#,##0', label: '#,##0;[Red]-#,##0（负数标红）' },
];

/**
 * 用 numfmt 库按格式串格式化数值。
 * - 格式串非法或格式化异常时回退为 String(value)，避免整表崩溃。
 */
const numfmtFormat = (value: number, pattern?: string): string => {
  const p = (pattern || '').trim() || DEFAULT_NUMFMT_PATTERN;
  try {
    return numfmt.format(p, value);
  } catch {
    return String(value);
  }
};

/** 校验 numfmt 格式串是否合法（供配置界面即时提示） */
export function isValidNumfmtPattern(pattern?: string | null): boolean {
  if (!pattern || !pattern.trim()) return false;
  try {
    return numfmt.isValidFormat(pattern.trim());
  } catch {
    return false;
  }
}

export const VALUE_FORMATS: PivotValueFormatDef[] = [
  {
    key: NUMFMT_FORMAT_KEY,
    label: '通用数字格式',
    format: (v, pattern) => numfmtFormat(v, pattern),
  },
  {
    key: 'duration_seconds',
    label: '秒 → 时间间隔',
    format: v => formatDurationFromSeconds(v),
  },
  {
    key: 'duration_minutes',
    label: '分钟 → 时间间隔',
    format: v => formatDurationFromSeconds((typeof v === 'number' ? v : Number(v)) * 60),
  },
];

const FORMAT_MAP = new Map<string, PivotValueFormatDef>(VALUE_FORMATS.map(d => [d.key, d]));

/** 按 key 获取格式定义 */
export function getPivotValueFormatDef(key?: string | null): PivotValueFormatDef | null {
  if (!key) return null;
  return FORMAT_MAP.get(key) ?? null;
}

/**
 * 统一的值格式化入口。
 * - formatFun 为空 → 原样字符串。
 * - duration_seconds / duration_minutes → 时间间隔。
 * - numfmt → 用 formatPattern（缺省 DEFAULT_NUMFMT_PATTERN）经 numfmt 库格式化。
 * - 其他任意 key（旧配置保存的 '0.00' / '#,##0' / '¥0.00' 等）→ 视为 numfmt
 *   格式串直接格式化，天然兼容历史数据。
 */
export function formatPivotValue(
  value: number,
  formatFun?: string | null,
  formatPattern?: string | null,
): string {
  if (value === null || value === undefined) return '';
  if (typeof value !== 'number' || !isFinite(value)) return String(value);
  if (!formatFun) return String(value);
  const def = FORMAT_MAP.get(formatFun);
  if (def) return def.format(value, formatPattern ?? undefined);
  // 兼容旧配置：未注册的 key 当作 numfmt 格式串
  return numfmtFormat(value, formatFun);
}

/**
 * 按 key 获取格式化函数（兼容旧调用方）。
 * 未注册的 key 视为 numfmt 格式串返回对应格式化函数。
 */
export function getPivotValueFormatFunc(key?: string | null): ((value: number) => string) | null {
  if (!key) return null;
  const def = FORMAT_MAP.get(key);
  if (def) return v => def.format(v, undefined);
  return v => numfmtFormat(v, key);
}

/** 按 key 获取展示名称 */
export function getPivotValueFormatLabel(key?: string | null): string {
  if (!key) return '';
  return FORMAT_MAP.get(key)?.label ?? key;
}

/**
 * 兼容旧配置：把历史保存的格式串 key（'0.00' / '#,##0' / '¥0.00' 等）
 * 归一为「通用数字格式 + formatPattern」，使配置界面下拉正确显示。
 * 已是注册 key（duration_* / numfmt）时不做任何处理。
 */
export function normalizeValueFormat(v: { formatFun?: string; formatPattern?: string }): void {
  if (!v.formatFun || FORMAT_MAP.has(v.formatFun)) return;
  v.formatPattern = v.formatPattern || v.formatFun;
  v.formatFun = NUMFMT_FORMAT_KEY;
}
